import { api } from "../services/config/api";
import type { Promotion } from "./promotions";
import { captureAppError } from "../observability/sentry";

const STORAGE_KEY = "hh_active_promos";
const TTL_MS = 30 * 60 * 1000; // 30 minutes — promos change occasionally
// FE-4: stale-as-fallback has a hard ceiling so severely outdated promo data is not served
const MAX_STALE_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

type CachedPromos = { promos: Promotion[]; timestamp: number };

let memory: CachedPromos | null = null;

function isCachedPromos(value: unknown): value is CachedPromos {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.timestamp === "number" && Array.isArray(v.promos);
}

function readStorage(): CachedPromos | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    // FE-10: runtime guard — discard malformed cache entries
    return isCachedPromos(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStorage(entry: CachedPromos) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (err) {
    // FE-11: report quota errors; silently drop other write failures
    if (err instanceof DOMException && err.name === "QuotaExceededError") {
      captureAppError(err, { area: "promoCache", action: "writeStorage" });
    }
  }
}

/**
 * Returns active promotions using a two-tier cache (in-memory → localStorage).
 * When the backend is unreachable, stale localStorage data is served so the
 * promo section stays visible instead of disappearing.
 *
 * Note: calls /promotions/active directly (not fetchActivePromotions) so we can
 * distinguish a real "no promos" empty array from a network failure.
 */
export async function getCachedPromotions(): Promise<Promotion[]> {
  const now = Date.now();

  if (memory && now - memory.timestamp < TTL_MS) return memory.promos;

  const stored = readStorage();
  if (stored && now - stored.timestamp < TTL_MS) {
    memory = stored;
    return stored.promos;
  }

  try {
    const res = await api.get<{ promotions: Promotion[] }>("/promotions/active");
    const promos = res.data.promotions ?? [];
    const entry: CachedPromos = { promos, timestamp: now };
    memory = entry;
    writeStorage(entry);
    return promos;
  } catch {
    // Backend down — serve stale rather than returning empty, but cap max staleness
    if (stored && now - stored.timestamp < MAX_STALE_LIFETIME_MS) {
      memory = stored;
      return stored.promos;
    }
    return [];
  }
}

export function invalidatePromoCache() {
  memory = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
